<script lang="ts" setup>
import type { AttachmentType, ColumnType } from 'nocodb-sdk'

/* interface */

const props = defineProps<{
  attachment: AttachmentType
  active?: boolean
  isExpanded?: boolean
  isFileContentMenuOpen: boolean
  selectedField: ColumnType
  attachmentIndex: number
}>()

const emits = defineEmits<{
  (e: 'update:isFileContentMenuOpen', value: boolean): void
  (e: 'expand', value: boolean): void
}>()
const isFileContentMenuOpen = useVModel(props, 'isFileContentMenuOpen', emits)

const { isExpanded, attachment, selectedField, attachmentIndex } = toRefs(props)

/* stores */
const { changedColumns, loadRow: _loadRow, row: _row } = useExpandedFormStoreOrThrow()

const { currentRow } = useSmartsheetRowStoreOrThrow()

const attachmentVModel = computed({
  get: () => {
    return _row.value.row[selectedField.value!.title!]
  },
  set: (val) => {
    if (val !== attachmentVModel.value) {
      currentRow.value.rowMeta.changed = true
      _row.value.row[selectedField.value!.title!] = val
      changedColumns.value.add(selectedField.value!.title!)
    }
  },
})

const refAttachmentCell = ref()

const isEditAllowed = computed(() => !!refAttachmentCell.value?.isEditAllowed)

/* file detection */

const fileEntry: ComputedRef<{ icon: keyof typeof iconMap; title: string | undefined }> = computed(() => {
  if (isImage(attachment.value.title || '', attachment.value.mimetype)) {
    return {
      icon: 'image',
      title: attachment.value.mimetype?.split('/')?.at(-1) || 'Image',
    }
  }

  if (isPdf(attachment.value.title || '', attachment.value.mimetype)) {
    return {
      icon: 'ncFileTypePdf',
      title: 'PDF',
    }
  }

  if (isVideo(attachment.value.title || '', attachment.value.mimetype)) {
    return {
      icon: 'ncFileTypeVideo',
      title: attachment.value.mimetype?.split('/')?.at(-1) || 'Video',
    }
  }

  if (isAudio(attachment.value.title || '', attachment.value.mimetype)) {
    return {
      icon: 'ncFileTypeAudio',
      title: attachment.value.mimetype?.split('/')?.at(-1) || 'Audio',
    }
  }

  if (isWord(attachment.value.title || '', attachment.value.mimetype)) {
    return {
      icon: 'ncFileTypeWord',
      title: 'Word',
    }
  }

  if (isExcel(attachment.value.title || '', attachment.value.mimetype)) {
    return {
      icon: 'ncFileTypeCsv',
      title: 'Excel',
    }
  }

  if (isPresentation(attachment.value.title || '', attachment.value.mimetype)) {
    return {
      icon: 'ncFileTypePresentation',
      title: 'PPT',
    }
  }

  if (isZip(attachment.value.title || '', attachment.value.mimetype)) {
    return {
      icon: 'ncFileTypeZip',
      title: 'Zip',
    }
  }

  return {
    icon: 'ncFileTypeUnknown',
    title: attachment.value.mimetype?.split('/')?.at(-1) || 'File',
  }
})

const showRenameInput = ref(false)

const attachmentTitle = ref('')

const isOpenContextMenu = ref(false)

function downloadCurrentFile() {
  refAttachmentCell.value?.downloadAttachment(attachment.value)
  isFileContentMenuOpen.value = false
}

function deleteCurrentFile() {
  refAttachmentCell.value?.removeAttachment(attachment.value.title, attachmentIndex.value)
  isFileContentMenuOpen.value = false
}

function updateAttachmentTitle() {
  if (!attachmentTitle.value?.trim()) return

  if (attachment.value.title === attachmentTitle.value) {
    return cancelRename()
  }

  refAttachmentCell.value?.updateAttachmentTitle(attachmentIndex.value, attachmentTitle.value)
  cancelRename()
}

function renameCurrentFile() {
  isFileContentMenuOpen.value = true
  attachmentTitle.value = attachment.value.title ?? ''
  showRenameInput.value = true
}

function cancelRename() {
  showRenameInput.value = false
  attachmentTitle.value = ''
  isFileContentMenuOpen.value = false
}

useEventListener(document, 'click', (e) => {
  if ((e.target as HTMLElement)?.closest('.nc-attachments-preview-bar, .nc-dropdown')) return

  emits('expand', false)
})

watch(isExpanded, (newValue) => {
  if (newValue || !isOpenContextMenu.value) return

  isOpenContextMenu.value = false
})

watch(isOpenContextMenu, (newValue) => {
  isFileContentMenuOpen.value = newValue
})
</script>

<template>
  <div
    class="h-[56px] border-1 rounded-md overflow-hidden hover:bg-nc-bg-gray-extralight cursor-pointer flex flex-row transition-all group"
    :class="{
      'w-[56px] border-nc-border-gray-medium': !isExpanded,
      'w-full border-transparent': isExpanded,
      '!border-primary shadow-selected preview-cell-active': props.active,
    }"
    style="scroll-margin-top: 28px"
  >
    <div class="hidden">
      <LazyCellAttachment ref="refAttachmentCell" v-model="attachmentVModel" />
    </div>
    <div class="flex flex-col shrink-0 relative">
      <div class="h-0 w-[56px] flex-1 relative">
        <LazyCellAttachmentPreviewThumbnail
          thumbnail="tiny"
          :attachment="attachment"
          class="object-cover transition-all duration-300 absolute overflow-hidden"
          :class="{
            'top-0 left-0 right-0 w-full h-[calc(100%-20px)] rounded-none': !isExpanded,
            'top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[36px] h-[36px] rounded-lg ring-1 ring-nc-gray-300':
              isExpanded,
          }"
        />
      </div>
      <div
        class="font-bold text-[12px] text-center uppercase truncate px-1 transition-all duration-300 absolute"
        :class="{
          'left-0 right-0 bottom-1': !isExpanded,
          'left-0 right-0 bottom-0 transform translate-y-full': isExpanded,
        }"
      >
        {{ fileEntry.title }}
      </div>
    </div>
    <div v-if="showRenameInput" class="flex-1 flex items-center pr-2">
      <a-input
        :ref="(el) => el?.focus?.()"
        v-model:value="attachmentTitle"
        class="!rounded-lg !w-auto flex-1"
        @blur="updateAttachmentTitle"
        @keyup.enter.prevent="updateAttachmentTitle"
        @keyup.esc="cancelRename"
      />
    </div>
    <div v-else class="whitespace-nowrap flex flex-col justify-center pl-1 w-[calc(100%_-_104px)]">
      <NcTooltip class="nc-preview-cell-title truncate max-w-full" show-on-truncate-only>
        <template #title>
          {{ attachment.title }}
        </template>
        {{ attachment.title }}
      </NcTooltip>
      <div class="text-xs text-gray-500">{{ getReadableFileSize(attachment.size!) }}</div>
    </div>
    <div v-if="isExpanded && !showRenameInput" class="self-center px-2">
      <NcButton
        v-if="!isEditAllowed"
        type="text"
        size="xs"
        class="!px-1 invisible group-hover:visible"
        :class="{
          '!visible': props.active,
        }"
        icon-only
        @click.stop="downloadCurrentFile"
      >
        <template #icon>
          <GeneralIcon icon="download" />
        </template>
      </NcButton>
      <NcDropdown v-else v-model:visible="isOpenContextMenu">
        <NcButton
          type="text"
          size="xs"
          class="!px-1 invisible group-hover:visible"
          :class="{
            '!visible': isOpenContextMenu || props.active,
          }"
          icon-only
          @click.stop
        >
          <template #icon>
            <GeneralIcon icon="threeDotVertical" />
          </template>
        </NcButton>

        <template #overlay>
          <NcMenu variant="small">
            <NcMenuItem v-if="isEditAllowed" @click="renameCurrentFile">
              <GeneralIcon icon="edit" />
              Rename
            </NcMenuItem>
            <NcMenuItem @click="downloadCurrentFile">
              <GeneralIcon icon="download" />
              Download
            </NcMenuItem>
            <template v-if="isEditAllowed">
              <NcDivider />
              <NcMenuItem danger @click="deleteCurrentFile">
                <GeneralIcon icon="delete" />
                Delete
              </NcMenuItem>
            </template>
          </NcMenu>
        </template>
      </NcDropdown>
    </div>
  </div>
</template>
